<template>
  <div class="flex h-full overflow-hidden relative" style="background-color: #fcfcff">
    <!-- Left: Tool categories sidebar + agent wrapper -->
    <div :class="leftColClass" class="overflow-hidden border-r border-stroke border-solid">
      <!-- Category icons (narrow sidebar) -->
      <div class="border-r border-stroke w-20 flex-shrink-0 p-2 flex flex-col gap-y-1 overflow-auto">
        <VDropdown
          v-for="item in buttonCategories"
          :key="item.key"
          ref="vdropdown"
          theme="flow"
          class="inline"
          placement="right-start"
          :distance="3"
          :triggers="['click']"
          auto-hide
          @apply-show="onDropdownApplyShow(item)"
          @apply-hide="onDropdownApplyHide(item)"
        >
          <div
            class="text-10 flex items-center justify-center flex-col cursor-pointer select-none hover:bg-gray-200 rounded-4 text-center px-1 p-2"
            :class="{ 'bg-gray-200': activeCategoryDropdown === item.key }"
          >
            <Icon v-if="item.icon" :name="item.icon" class="w-6 h-6 mb-1" :class="{ [item.color]: true }" />
            <span class="font-500">{{ item.name }}</span>
          </div>
          <template #popper>
            <div class="w-200 px-2 py-2" style="background-color: #fcfcff">
              <div
                v-for="button in getButtons(item.key)"
                :key="button.key"
                draggable="true"
                class="px-4 rounded-4 pt-2 pb-2 font-500 text-12 flex gap-x-2 cursor-pointer hover:cursor-grab select-none tracking-wide text-semi-dark-text hover:bg-gray-200"
                @dragstart="onButtonDragStart($event, button.key)"
                @click="onButtonDblClick(button.key)"
              >
                <Icon :name="button.icon" class="w-5 h-5 flex-shrink-0" :class="{ [item.color]: true }" />
                <div class="flex items-center">{{ $t(`evaluate_editor.${button.key}`) }}</div>
              </div>
            </div>
          </template>
        </VDropdown>
      </div>

      <!-- Agent wrapper (centre column) -->
      <div class="flex flex-col flex-grow overflow-hidden">
        <!-- Agent header row -->
        <div class="bg-white py-2 h-12 shadow-card flex items-center pl-3 pr-3 border-b border-stroke border-solid flex-shrink-0">
          <Icon name="memory" class="w-5 h-5 mr-2 text-purple-500 flex-shrink-0" />
          <span class="text-13 font-600 mr-auto text-purple-700">AI Agent</span>
          <PillButton text="Configure Agent" small :primary="selectedItemId === agentItem.id" :outlined="selectedItemId !== agentItem.id" @click="onAgentHeaderClick" />
        </div>

        <!-- Tools drop zone -->
        <div class="flex-grow overflow-auto px-3 py-3">
          <div class="border-2 border-dashed border-purple-200 rounded-8 min-h-40 p-2">
            <div class="text-11 text-gray-400 font-500 mb-2 text-center uppercase tracking-wide">Tools</div>
            <template v-if="agentTools.length">
              <div v-for="(tool, index) in agentTools" :key="tool.id">
                <EvaluateDropZone v-if="index === 0" :accept="acceptList" :evaluate="agentTools" :index="index" @evaluate-update="onToolsUpdate" />
                <EvaluateItem
                  :evaluate="agentTools"
                  :selected-evaluate-item-id="selectedItemId"
                  :item="tool"
                  @update="onToolUpdated($event, index)"
                  @item-click="onToolClicked"
                  @evaluate-update="onToolsUpdate"
                />
                <EvaluateDropZone :accept="acceptList" :evaluate="agentTools" :index="index + 1" @evaluate-update="onToolsUpdate" />
              </div>
            </template>
            <template v-else>
              <EvaluateDropZone class="!min-h-24" :accept="acceptList" :evaluate="agentTools" :index="0" @evaluate-update="onToolsUpdate" />
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Right: Config panel -->
    <div :class="rightColClass" class="flex flex-col overflow-hidden">
      <template v-if="selectedItem">
        <!-- Header row (fixed height) -->
        <div class="px-6 py-2 h-12 flex justify-between flex-shrink-0 shadow-card items-center border-stroke border-solid border-b">
          <div class="flex items-center">
            <IconButton icon="chevron_left" class="-ml-4 lg:hidden" @click="selectedItem = null" />
            <span class="text-16 flex-grow capitalize">{{ getItemTitle(selectedItem) }}</span>
          </div>
          <IconButton v-if="selectedItem.id !== agentItem.id" icon="delete" class="text-alert" @click="onRemoveTool" />
        </div>
        <!-- Scrollable config area -->
        <AgentToolDefinitionSection
          v-if="selectedItem.id !== agentItem.id"
          class="mx-3 mt-3 flex-shrink-0"
          :tool-description="selectedItem.tool_description || ''"
          :policy="selectedItem.policy || ''"
          :tool-type="selectedItem.type"
          @update="onToolDefinitionUpdated"
        />
        <div class="flex-grow min-h-0">
          <component :is="configComponent" :item="selectedItem" :is-agent="selectedItem.id !== agentItem.id" @valid="onValidChange" @update="onItemUpdated" />
        </div>
      </template>
      <div v-else class="hidden xl:flex flex-col items-center justify-center h-full text-gray-400 text-13">
        <Icon name="memory" class="w-10 h-10 mb-2 text-purple-300" />
        <div>Click the agent header or a tool to configure</div>
      </div>
    </div>
  </div>
</template>

<script>
import EvaluateItems, { Categories } from './evaluate/EvaluateItems';
import { createEvaluateItem } from './evaluate/EvaluateItemsConfig';
import EvaluateDropZone from './evaluate/EvaluateDropZone.vue';
import AgentToolDefinitionSection from './evaluate/AgentToolDefinitionSection.vue';
import DeleteModal from '../modals/DeleteModal.vue';

const AGENT_TOOL_CATEGORIES = ['data', 'action', 'document'];

const AGENT_ACCEPT_LIST = [
  'ChartOverview',
  'DataAdapterOverview',
  'ApiOverview',
  'ActionApiOverview',
  'EmailOverview',
  'ActionEmailOverview',
  'DocumentHTMLOverview',
  'DocumentImageOverview',
  'DocumentPDFOverview',
  'ActionScheduleOverview',
  'DataTransformOverview',
  'FlowInvokeOverview',
];

export default {
  name: 'AgentEvaluateEditor',
  components: { EvaluateDropZone, AgentToolDefinitionSection },
  props: {
    agentItem: {
      type: Object,
      required: true,
    },
  },
  emits: ['update'],
  data() {
    return {
      selectedItem: null,
      selectedItemId: null,
      activeCategoryDropdown: null,
      acceptList: AGENT_ACCEPT_LIST,
    };
  },
  computed: {
    buttonCategories() {
      return Categories.filter((c) => AGENT_TOOL_CATEGORIES.includes(c.key)).map((category) => ({
        ...category,
        name: this.$t(`evaluate_editor.${category.key}`),
      }));
    },
    agentTools() {
      return this.agentItem?.payload?.tools || [];
    },
    configComponent() {
      if (!this.selectedItem) return null;
      if (this.selectedItem.id === this.agentItem.id) {
        return EvaluateItems['ai-agent']?.configComponent || null;
      }
      return EvaluateItems[this.selectedItem.type]?.configComponent || null;
    },
    leftColClass() {
      return ['flex flex-row flex-grow xl:max-w-1/3 xxl:max-w-1/2', this.selectedItem ? 'hidden xl:flex' : ''];
    },
    rightColClass() {
      return ['flex-grow xl:max-w-2/3 xxl:max-w-1/2 border-l border-stroke border-solid', this.selectedItem ? '' : 'hidden xl:flex'];
    },
  },
  methods: {
    getButtons(category) {
      return Object.values(EvaluateItems).filter((item) => item.category === category);
    },
    getItemTitle(item) {
      if (item.id === this.agentItem.id) return 'AI Agent';
      return this.$t(`evaluate_editor.${item.type}`);
    },
    onDropdownApplyShow(item) {
      this.activeCategoryDropdown = item.key;
    },
    onDropdownApplyHide() {
      this.activeCategoryDropdown = null;
    },
    onButtonDragStart(e, type) {
      const item = createEvaluateItem(type);
      e.dataTransfer.setData('id', item.id);
      e.dataTransfer.setData('item', JSON.stringify(item));
      const component = EvaluateItems[type]?.component || null;
      if (component?.name) e.dataTransfer.setData('type', component.name);
    },
    onButtonDblClick(type) {
      const item = createEvaluateItem(type);
      if (this.$refs.vdropdown) {
        this.$refs.vdropdown.forEach((dd) => dd.hide?.());
      }
      const updatedTools = [...this.agentTools, item];
      this.emitAgentUpdate(updatedTools);
      this.$nextTick(() => {
        this.selectedItem = item;
        this.selectedItemId = item.id;
      });
    },
    onAgentHeaderClick() {
      if (this.selectedItemId === this.agentItem.id) {
        this.selectedItem = null;
        this.selectedItemId = null;
      } else {
        this.selectedItem = this.agentItem;
        this.selectedItemId = this.agentItem.id;
      }
    },
    onToolClicked(_, tool) {
      if (tool.id === this.selectedItemId) {
        this.selectedItem = null;
        this.selectedItemId = null;
      } else {
        this.selectedItem = null;
        this.$nextTick(() => {
          this.selectedItem = tool;
          this.selectedItemId = tool.id;
        });
      }
    },
    onToolUpdated(tool, index) {
      const tools = [...this.agentTools];
      tools[index] = tool;
      this.emitAgentUpdate(tools);
    },
    onToolsUpdate(updatedEvaluate) {
      const tools = Array.isArray(updatedEvaluate) ? updatedEvaluate : this.agentTools;
      this.emitAgentUpdate(tools);
    },
    onItemUpdated(item) {
      if (item.id === this.agentItem.id) {
        const updated = { ...this.agentItem, payload: item.payload };
        this.$emit('update', updated);
        this.selectedItem = updated;
      } else {
        const tools = [...this.agentTools];
        const idx = tools.findIndex((t) => t.id === item.id);
        if (idx !== -1) {
          tools[idx] = { ...tools[idx], payload: item.payload };
          if (this.selectedItem?.id === item.id) this.selectedItem = tools[idx];
          this.emitAgentUpdate(tools);
        }
      }
    },
    onToolDefinitionUpdated(toolDef) {
      const tools = [...this.agentTools];
      const idx = tools.findIndex((t) => t.id === this.selectedItemId);
      if (idx !== -1) {
        tools[idx] = { ...tools[idx], ...toolDef };
        this.selectedItem = tools[idx];
        this.emitAgentUpdate(tools);
      }
    },
    emitAgentUpdate(tools) {
      const updated = {
        ...this.agentItem,
        payload: { ...this.agentItem.payload, tools },
      };
      this.$emit('update', updated);
    },
    onRemoveTool() {
      const toolId = this.selectedItemId;
      const toolName = this.selectedItem ? this.getItemTitle(this.selectedItem) : '';
      this.$showModal(DeleteModal, {
        subtitle: `Are you sure you want to remove the <b>${toolName}</b> tool from this agent?`,
        info: null,
        onConfirm: () => {
          const tools = this.agentTools.filter((t) => t.id !== toolId);
          this.selectedItem = null;
          this.selectedItemId = null;
          this.emitAgentUpdate(tools);
        },
      });
    },
    onValidChange() {},
  },
  watch: {
    agentItem: {
      handler(newVal) {
        if (this.selectedItemId === newVal?.id) {
          this.selectedItem = newVal;
        }
      },
      deep: true,
    },
  },
};
</script>
